import React from 'react';
import styled from '@emotion/styled';
import { Button } from '@bblabs/module-library/atoms';
import { colors, heavy } from '@bblabs/module-library/styles';
import { SpeechBubble, ThumbsUpOutline, VideoCameraIcon } from '@bblabs/module-library/svgs';
import { VideoButtonItem } from '@bblabs/module-library/molecules';
import Replay from './ReplaySVG';
import { useWindowSize } from '@/hooks/useWindowSize';

type ReactionsEndScreenProps = {
  className?: string;
  likeButtonClickHandler?: VoidFunction;
  replyButtonClickHandler?: VoidFunction;
  replyWithVideoButtonClickHandler?: VoidFunction;
  replayButtonClickHandler?: VoidFunction;
  videoOwner: string;
};

/**
 * End screen for the video player when reactions are available
 */
const ReactionsEndScreen = ({
  className = '',
  likeButtonClickHandler = () => {},
  replyButtonClickHandler = () => {},
  replyWithVideoButtonClickHandler = () => {},
  replayButtonClickHandler = () => {},
  videoOwner,
}: ReactionsEndScreenProps) => {
  const [size] = useWindowSize<'normal' | 'small'>({
    defaultSize: 'normal',
    resizeHandler: (windowSize) => {
      if (windowSize <= 390) {
        return 'small';
      }
      return 'normal';
    },
  });

  return (
    <div className={`dynamic-video-endscreen ${className} ${size}`}>
      {size === 'normal' && (
        <div className="endscreen-container">
          <h2>
            Choose a reply for&nbsp;
            {videoOwner}
          </h2>
          <div className="buttons-container">
            <Button
              label="Send a Like"
              onClick={likeButtonClickHandler}
              size="large"
              iconPosition="before"
              icon={<ThumbsUpOutline size={16 / 2.85} fill={colors.white} />}
            />
            <Button
              label="Leave a Reply"
              onClick={replyButtonClickHandler}
              size="large"
              iconPosition="before"
              icon={<SpeechBubble size={`${19 / 2.85}`} fill={colors.white} />}
            />
          </div>
          <div className="divider">
            <div />
            <span>Or</span>
            <div />
          </div>
          <h3>
            Send&nbsp;
            {videoOwner}
            &nbsp;a Video
          </h3>
          <Button
            label="Reply with a Personalized Video"
            onClick={replyWithVideoButtonClickHandler}
            size="large"
            iconPosition="before"
            icon={<VideoCameraIcon size={12 / 2.85} fill={colors.white} />}
          />
        </div>
      )}
      {size === 'small' && (
        <div className="small-endscreen-container">
          <VideoButtonItem
            className="replay"
            SvgElement={Replay}
            handleClick={replayButtonClickHandler}
            text="Replay"
            name=""
            isRebrand={false}
          />
          <VideoButtonItem
            className="like"
            SvgElement={ThumbsUpOutline}
            handleClick={likeButtonClickHandler}
            text="Like"
            name=""
            isRebrand={false}
          />
          <VideoButtonItem
            className="reply-with-video"
            SvgElement={VideoCameraIcon}
            handleClick={replyWithVideoButtonClickHandler}
            text="Reply"
            name=""
            isRebrand={false}
          />
        </div>
      )}
    </div>
  );
};

const styledReactionsEndScreen = styled(ReactionsEndScreen)`
  align-items: center;
  display: flex;
  height: 100%;
  font-size: 16px;

  .buttons-container {
    display: flex;
    margin: 0.75rem 0 0 0;

    button {
      margin: 0 0 0 1rem;

      &:first-of-type {
        margin: 0;
      }

      span {
        white-space: nowrap;
      }
    }
  }

  .divider {
    align-items: center;
    display: flex;
    margin: 1rem 0 0;
    width: 100%;

    div {
      background-color: ${colors.white};
      flex-grow: 1;
      height: 0.125rem;
    }
    span {
      color: ${colors.white};
      display: inline-block;
      ${heavy()}
      font-size: 0.875rem;
      line-height: 1.125rem;
      margin: 0 0.5rem;
    }
  }

  /* Full end screen container */
  .endscreen-container {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin: 0;
    max-width: 21.75rem;

    h2 {
      ${heavy()}
      font-size: 1.5rem;
      line-height: 2rem;
      margin: 0;
    }

    svg * {
      fill: ${colors.battleship};
      transition: 0.3s; /* match the transition for ML's button */
    }

    h3 {
      color: ${colors.white};
      ${heavy()}
      font-size: 1.125rem;
      margin: 0.75rem 0 1.25rem;
      text-transform: none;
    }

    button {
      background-color: ${colors.white};
      border-radius: 0.25rem;
      color: ${colors.battleship};
      font-size: 0.875rem;

      &:hover,
      &:focus {
        background-color: ${colors.white};
        color: ${colors.taosSky};
        outline-color: ${colors.taosSky};

        svg * {
          fill: ${colors.taosSky};
        }
      }
    }
  }

  .small-endscreen-container {
    display: flex;
    flex-direction: row;
    gap: 21px;
    align-items: center;
    box-sizing: border-box;
    overflow: hidden;
    height: 100%;
    width: 100%;
    color: ${colors.white};

    button {
      margin: 0;
      > span {
        color: ${colors.white};
        ${heavy()};
        font-size: ${14 / 16}rem;
      }

      .circle {
        height: ${57 / 16}rem;
        width: ${57 / 16}rem;
      }

      &.reply-with-video .circle svg {
        max-height: 18px;
      }
      &.like .circle svg {
        max-height: 24px;
      }
      &.replay .circle svg {
        max-height: 31px;
      }
    }
  }

  @media screen and (max-width: 737px) {
    .endscreen-container {
      transform: scale(0.8);
    }
  }
  @media screen and (max-width: 470px) {
    .endscreen-container {
      transform: scale(0.6);
    }
  }
`;

export default styledReactionsEndScreen;
